import * as React from 'react'
import { useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { classNames } from 'primereact/utils'
import { useEventListener, useOnClickOutside } from 'usehooks-ts'
import InfoIcon from '../../assets/images/auth/info-circle.svg'
import { Residency } from '../../models/Residency'
import { DATA_REGION_CONTENTS, RESIDENCY_OPTIONS, residencyLabel } from './residency'
import './Auth.scss'

interface ResidencyStepProps {
  value: Residency
  onChange: (residency: Residency) => void
  onSubmit: () => void
  loading: boolean
  disabled?: boolean
  disabledMessage?: string
}

export const ResidencyStep: React.FC<ResidencyStepProps> = ({ value, onChange, onSubmit, loading, disabled, disabledMessage }) => {
  const [confirming, setConfirming] = useState<boolean>(false)

  return (
    <div className='residency-step'>
      <div className='residency-intro'>
        <h2 className='residency-title'>
          Choose where
          <br />
          your data is stored
        </h2>
        <div className='residency-subtitle'>
          Set the country where Zocks stores your meeting and client data. <DataRegionInfo />
        </div>
      </div>
      <div className='residency-options-group'>
        <ResidencySelector value={value} onChange={onChange} disabled={loading} />
        {!confirming && (
          <div className='residency-notice'>
            <img src={InfoIcon} className='residency-notice-icon' alt='' />
            <span className='residency-notice-text'>You can't change your data region later.</span>
          </div>
        )}
      </div>
      {confirming ? (
        <div className='residency-confirm-card'>
          <div className='residency-confirm-text'>You chose {residencyLabel(value)}. You can't change your data region later.</div>
          <Button className='auth-email-button residency-submit' onClick={onSubmit} loading={loading} disabled={disabled}>
            <span className='auth-email-button-text'>Confirm and create account</span>
          </Button>
          {disabled && disabledMessage && <div className='residency-notice-text'>{disabledMessage}</div>}
        </div>
      ) : (
        <Button className='auth-email-button residency-submit' onClick={() => setConfirming(true)} disabled={disabled || loading}>
          <span className='auth-email-button-text'>Create account</span>
        </Button>
      )}
    </div>
  )
}

interface ResidencySelectorProps {
  value: Residency
  onChange: (residency: Residency) => void
  disabled?: boolean
}

export const ResidencySelector: React.FC<ResidencySelectorProps> = ({ value, onChange, disabled }) => (
  <div className='residency-options' role='radiogroup' aria-label='Data residency'>
    {RESIDENCY_OPTIONS.map(({ value: residency, label, flag }) => (
      <label
        key={residency}
        className={classNames('residency-option', { 'residency-option-selected': value === residency })}
        htmlFor={`residency-${residency}`}
      >
        <input
          className='residency-radio'
          type='radio'
          id={`residency-${residency}`}
          name='residency'
          value={residency}
          checked={value === residency}
          disabled={disabled}
          onChange={() => onChange(residency)}
        />
        <img src={flag} className='residency-flag' alt='' />
        <span className='residency-option-label'>{label}</span>
      </label>
    ))}
  </div>
)

const DataRegionInfo: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false)
  const containerRef = useRef<HTMLSpanElement>(null)
  useOnClickOutside(containerRef, () => setOpen(false))
  useEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setOpen(false)
    }
  })

  return (
    <span className='residency-info' ref={containerRef}>
      <button className='residency-info-button' type='button' aria-expanded={open} onClick={() => setOpen((isOpen) => !isOpen)}>
        What's stored here?
      </button>
      {open && (
        <div className='residency-info-popover'>
          <div className='residency-info-popover-title'>What's stored in your data region</div>
          <ul className='residency-info-popover-list'>
            {DATA_REGION_CONTENTS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </span>
  )
}
